import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  InfoTooltip,
  Input,
  Loading,
  Modal,
  Select,
  Stat,
  StatusBadge,
  Table,
  Td,
  Textarea,
  Th,
  cx,
} from '../components/ui';
import {
  api,
  type Campaign,
  type Channel,
  type Funnel,
  type Persona,
  type Prospect,
  type RoiRow,
  type SequenceStep,
} from '../lib/api';
import { money, percent, relative } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

type Tab = 'sequence' | 'personas' | 'prospects' | 'analytics';

interface CampaignDetailResponse {
  campaign: Campaign;
  personas: Persona[];
  sequence_steps: SequenceStep[];
}

export function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('sequence');
  const detail = useApi<CampaignDetailResponse>(id ? `/api/campaigns/${id}` : null);

  if (detail.loading) return <Loading />;
  if (detail.error) return <ErrorNote message={detail.error} onRetry={detail.reload} />;
  if (!detail.data || !id) return null;

  const { campaign, personas, sequence_steps: steps } = detail.data;

  const tabs: Array<[Tab, string]> = [
    ['sequence', `Sequence (${steps.length})`],
    ['personas', `Personas (${personas.length})`],
    ['prospects', 'Prospects'],
    ['analytics', 'Analytics'],
  ];

  return (
    <>
      <div className="mb-4">
        <Link to="/campaigns" className="text-xs font-medium text-accent-600 hover:underline">
          ← Campaigns
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-ink-900">{campaign.name}</h1>
          <StatusBadge status={campaign.status} />
        </div>
        {campaign.description && <p className="mt-0.5 text-sm text-ink-600">{campaign.description}</p>}
      </div>

      <div className="mb-4 flex gap-1 border-b border-ink-200">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cx(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-accent-600 text-accent-700'
                : 'border-transparent text-ink-500 hover:border-ink-300 hover:text-ink-800',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'sequence' && <SequenceTab campaignId={id} steps={steps} onSaved={detail.reload} />}
      {tab === 'personas' && (
        <PersonasTab campaignId={id} personas={personas} onChanged={detail.reload} />
      )}
      {tab === 'prospects' && <ProspectsTab campaignId={id} />}
      {tab === 'analytics' && <AnalyticsTab campaignId={id} />}

      {tab === 'sequence' && <SendWindowEditor campaign={campaign} onSaved={detail.reload} />}
      {tab === 'sequence' && <IcpPicker campaign={campaign} onSaved={detail.reload} />}
      {tab === 'sequence' && <ContextEditor campaign={campaign} onSaved={detail.reload} />}
    </>
  );
}

const minutesToTime = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
const timeToMinutes = (time: string): number => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const WEEKDAYS: Array<[number, string]> = [
  [1, 'Mon'],
  [2, 'Tue'],
  [3, 'Wed'],
  [4, 'Thu'],
  [5, 'Fri'],
  [6, 'Sat'],
  [7, 'Sun'],
];

function SendWindowEditor({ campaign, onSaved }: { campaign: Campaign; onSaved: () => void }) {
  const [days, setDays] = useState<number[]>(campaign.send_days ?? [2, 3, 4]);
  const [start, setStart] = useState(minutesToTime(campaign.send_window_start ?? 510));
  const [end, setEnd] = useState(minutesToTime(campaign.send_window_end ?? 660));
  const [fallbackTz, setFallbackTz] = useState(campaign.default_timezone ?? '');
  const [dailyCap, setDailyCap] = useState(
    campaign.daily_send_cap != null ? String(campaign.daily_send_cap) : '',
  );

  const dirty =
    JSON.stringify(days) !== JSON.stringify(campaign.send_days ?? [2, 3, 4]) ||
    start !== minutesToTime(campaign.send_window_start ?? 510) ||
    end !== minutesToTime(campaign.send_window_end ?? 660) ||
    fallbackTz !== (campaign.default_timezone ?? '') ||
    dailyCap !== (campaign.daily_send_cap != null ? String(campaign.daily_send_cap) : '');
  const dailyCapValid = dailyCap.trim() === '' || (Number.isInteger(Number(dailyCap)) && Number(dailyCap) >= 1);

  const save = useAction(async () => {
    const parsedCap = dailyCap.trim() === '' ? null : Number(dailyCap);
    await api.put(`/api/campaigns/${campaign.id}`, {
      send_days: days,
      send_window_start: timeToMinutes(start),
      send_window_end: timeToMinutes(end),
      default_timezone: fallbackTz.trim(),
      daily_send_cap: parsedCap,
    });
    onSaved();
  });

  return (
    <Card title="Send window" className="mt-4">
      <p className="mb-3 text-sm text-ink-600">
        "Schedule best time" lands emails inside this window, in each recipient's own timezone —
        detected by Claude from their email domain, company and location fields. Tuesday to Thursday
        mornings are where B2B open rates concentrate. The daily send cap limits this campaign alone —
        useful when it shares a mailbox with another campaign and should only take a fixed slice of it.
      </p>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-500">Days</p>
          <div className="flex gap-1">
            {WEEKDAYS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() =>
                  setDays((current) =>
                    current.includes(value)
                      ? current.filter((day) => day !== value)
                      : [...current, value].sort(),
                  )
                }
                className={cx(
                  'rounded-md px-2.5 py-1.5 text-xs font-medium ring-1 ring-inset transition-colors',
                  days.includes(value)
                    ? 'bg-accent-600 text-white ring-accent-600'
                    : 'bg-white text-ink-600 ring-ink-200 hover:bg-ink-50',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="w-28">
          <Field label="From">
            <Input type="time" value={start} onChange={(event) => setStart(event.target.value)} />
          </Field>
        </div>
        <div className="w-28">
          <Field label="To">
            <Input type="time" value={end} onChange={(event) => setEnd(event.target.value)} />
          </Field>
        </div>
        <div className="w-56">
          <Field label="Fallback timezone" hint="Used when a recipient's is unknown.">
            <Input
              value={fallbackTz}
              onChange={(event) => setFallbackTz(event.target.value)}
              placeholder="Europe/Paris"
            />
          </Field>
        </div>
        <div className="w-36">
          <Field label="Daily send cap" hint="Blank = unlimited.">
            <Input
              type="number"
              min={1}
              value={dailyCap}
              onChange={(event) => setDailyCap(event.target.value)}
              placeholder="Unlimited"
            />
          </Field>
        </div>
      </div>

      {save.error && <div className="mt-2"><ErrorNote message={save.error} /></div>}
      {dirty && (
        <Button
          variant="primary"
          className="mt-3"
          loading={save.pending}
          disabled={days.length === 0 || !dailyCapValid}
          onClick={() => void save.run()}
        >
          Save send window
        </Button>
      )}
    </Card>
  );
}

function IcpPicker({ campaign, onSaved }: { campaign: Campaign; onSaved: () => void }) {
  const library = useApi<{ icps: Array<{ key: string; label: string }>; default_key: string }>(
    '/api/campaigns/icps',
  );
  const defaultKey = library.data?.default_key ?? '';
  const current = campaign.icp_key ?? defaultKey;
  const [icpKey, setIcpKey] = useState(current);
  const save = useAction(async () => {
    // Saving the default back stores nothing, so the campaign keeps following it.
    await api.put(`/api/campaigns/${campaign.id}`, { icp_key: icpKey === defaultKey ? '' : icpKey });
    onSaved();
  });

  if (!library.data) return null;

  return (
    <Card title="Ideal customer" className="mt-4">
      <p className="mb-2 text-sm text-ink-600">
        Which ICP from the company profile this campaign targets. LinkedIn qualification scores
        candidates against it when you assign a batch to this campaign.
      </p>
      <Select value={icpKey} onChange={(event) => setIcpKey(event.target.value)}>
        {library.data.icps.map((icp) => (
          <option key={icp.key} value={icp.key}>
            {icp.label}
            {icp.key === defaultKey ? ' (default)' : ''}
          </option>
        ))}
      </Select>
      {save.error && <div className="mt-2"><ErrorNote message={save.error} /></div>}
      {icpKey !== current && (
        <Button variant="primary" className="mt-2" loading={save.pending} onClick={() => void save.run()}>
          Save ideal customer
        </Button>
      )}
    </Card>
  );
}

function ContextEditor({ campaign, onSaved }: { campaign: Campaign; onSaved: () => void }) {
  const [context, setContext] = useState(campaign.context);
  const save = useAction(async () => {
    await api.put(`/api/campaigns/${campaign.id}`, { context });
    onSaved();
  });

  return (
    <Card title="Campaign context" className="mt-4">
      <p className="mb-2 text-sm text-ink-600">
        This is prepended to every Claude prompt for this campaign — the offer, the proof points, and
        anything Claude must never claim.
      </p>
      <Textarea rows={6} value={context} onChange={(event) => setContext(event.target.value)} />
      {save.error && <div className="mt-2"><ErrorNote message={save.error} /></div>}
      {context !== campaign.context && (
        <Button variant="primary" className="mt-2" loading={save.pending} onClick={() => void save.run()}>
          Save context
        </Button>
      )}
    </Card>
  );
}

function SequenceTab({
  campaignId,
  steps,
  onSaved,
}: {
  campaignId: string;
  steps: SequenceStep[];
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<SequenceStep[]>(
    steps.map((step) => ({
      day_offset: step.day_offset,
      channel: step.channel,
      message_type: step.message_type,
      skip_if_replied: step.skip_if_replied,
      instructions: step.instructions,
    })),
  );

  const save = useAction(async () => {
    await api.put(`/api/campaigns/${campaignId}/sequence`, { steps: draft });
    onSaved();
  });

  function update(index: number, patch: Partial<SequenceStep>) {
    setDraft((current) => current.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  }

  function add() {
    const lastDay = draft.at(-1)?.day_offset ?? -3;
    setDraft((current) => [
      ...current,
      {
        day_offset: lastDay + 3,
        channel: 'email',
        message_type: current.length === 0 ? 'initial_outreach' : `follow_up_${current.length}`,
        skip_if_replied: true,
        instructions: '',
      },
    ]);
  }

  return (
    <Card
      title="Cadence"
      action={
        <div className="flex gap-2">
          <Button size="sm" onClick={add}>
            Add step
          </Button>
          <Button size="sm" variant="primary" loading={save.pending} onClick={() => void save.run()}>
            Save sequence
          </Button>
        </div>
      }
    >
      <p className="mb-3 text-sm text-ink-600">
        Day 0 is the first send. When a step comes due, Claude drafts it and it lands in your review
        queue — nothing sends on its own.
      </p>

      {draft.length === 0 ? (
        <EmptyState
          title="No cadence yet"
          description="A common shape: email on day 0, LinkedIn DM on day 3, post engagement on day 7, final email on day 14."
          action={<Button variant="primary" onClick={add}>Add the first step</Button>}
        />
      ) : (
        <div className="space-y-3">
          {draft.map((step, index) => (
            <div key={index} className="rounded-md border border-ink-200 p-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-24">
                  <Field label="Day">
                    <Input
                      type="number"
                      min={0}
                      value={step.day_offset}
                      onChange={(event) => update(index, { day_offset: Number(event.target.value) })}
                    />
                  </Field>
                </div>
                <div className="w-52">
                  <Field label="Channel">
                    <Select
                      value={step.channel}
                      onChange={(event) => update(index, { channel: event.target.value as Channel })}
                    >
                      <option value="email">Email</option>
                      <option value="linkedin_dm">LinkedIn DM</option>
                      <option value="linkedin_post_engage">LinkedIn post</option>
                    </Select>
                  </Field>
                </div>
                <div className="min-w-[10rem] flex-1">
                  <Field
                    label="Message type"
                    tip="A free-text label Claude uses to tell steps apart — e.g. initial_outreach, follow_up_1. It shapes the drafting prompt, so keep it descriptive."
                  >
                    <Input
                      value={step.message_type}
                      onChange={(event) => update(index, { message_type: event.target.value })}
                    />
                  </Field>
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={step.skip_if_replied}
                    onChange={(event) => update(index, { skip_if_replied: event.target.checked })}
                    className="rounded border-ink-300"
                  />
                  Skip if replied
                  <InfoTooltip content="If the prospect has replied by the time this step comes due, it won't be drafted." />
                </label>
                <Button
                  size="sm"
                  variant="danger"
                  className="mb-1"
                  onClick={() => setDraft((current) => current.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
              <div className="mt-2">
                <Field label="Instructions for this step">
                  <Input
                    value={step.instructions}
                    onChange={(event) => update(index, { instructions: event.target.value })}
                    placeholder="Reference the first email without repeating the pitch."
                  />
                </Field>
              </div>
            </div>
          ))}
        </div>
      )}

      {save.error && <div className="mt-3"><ErrorNote message={save.error} /></div>}
    </Card>
  );
}

function PersonasTab({
  campaignId,
  personas,
  onChanged,
}: {
  campaignId: string;
  personas: Persona[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<Persona | 'new' | null>(null);

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button variant="primary" onClick={() => setEditing('new')}>
          Add persona
        </Button>
      </div>

      {personas.length === 0 ? (
        <EmptyState
          title="No personas yet"
          description="A persona carries the prompts Claude uses for a type of buyer — their titles, their pain, and how you talk to them."
          action={<Button variant="primary" onClick={() => setEditing('new')}>Create a persona</Button>}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {personas.map((persona) => (
            <Card
              key={persona.id}
              title={persona.name}
              action={
                <Button size="sm" onClick={() => setEditing(persona)}>
                  Edit
                </Button>
              }
            >
              {persona.description && <p className="text-sm text-ink-600">{persona.description}</p>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {persona.job_titles.map((title) => (
                  <Badge key={title}>{title}</Badge>
                ))}
                {persona.industry_focus.map((industry) => (
                  <Badge key={industry} tone="blue">
                    {industry}
                  </Badge>
                ))}
              </div>
              <p className="mt-3 text-xs text-ink-500">
                {persona.prospect_count ?? 0} prospects assigned
              </p>
              {persona.email_prompt && (
                <p className="mt-2 line-clamp-3 rounded bg-ink-50 p-2 text-xs text-ink-600">
                  {persona.email_prompt}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      <PersonaModal
        open={editing !== null}
        persona={editing === 'new' ? null : editing}
        campaignId={campaignId}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          onChanged();
        }}
      />
    </>
  );
}

function PersonaModal({
  open,
  persona,
  campaignId,
  onClose,
  onDone,
}: {
  open: boolean;
  persona: Persona | null;
  campaignId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: persona?.name ?? '',
    description: persona?.description ?? '',
    job_titles: (persona?.job_titles ?? []).join(', '),
    industry_focus: (persona?.industry_focus ?? []).join(', '),
    email_prompt: persona?.email_prompt ?? '',
    linkedin_dm_prompt: persona?.linkedin_dm_prompt ?? '',
    linkedin_post_idea_prompt: persona?.linkedin_post_idea_prompt ?? '',
  });

  // Re-seed the form when a different persona is opened.
  const [seededFor, setSeededFor] = useState(persona?.id ?? null);
  if (open && (persona?.id ?? null) !== seededFor) {
    setSeededFor(persona?.id ?? null);
    setForm({
      name: persona?.name ?? '',
      description: persona?.description ?? '',
      job_titles: (persona?.job_titles ?? []).join(', '),
      industry_focus: (persona?.industry_focus ?? []).join(', '),
      email_prompt: persona?.email_prompt ?? '',
      linkedin_dm_prompt: persona?.linkedin_dm_prompt ?? '',
      linkedin_post_idea_prompt: persona?.linkedin_post_idea_prompt ?? '',
    });
  }

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const save = useAction(async () => {
    const payload = {
      campaign_id: campaignId,
      name: form.name,
      description: form.description,
      job_titles: form.job_titles.split(',').map((value) => value.trim()).filter(Boolean),
      industry_focus: form.industry_focus.split(',').map((value) => value.trim()).filter(Boolean),
      email_prompt: form.email_prompt,
      linkedin_dm_prompt: form.linkedin_dm_prompt,
      linkedin_post_idea_prompt: form.linkedin_post_idea_prompt,
    };
    if (persona) await api.put(`/api/personas/${persona.id}`, payload);
    else await api.post('/api/personas', payload);
    onDone();
  });

  const remove = useAction(async () => {
    if (persona) await api.del(`/api/personas/${persona.id}`);
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title={persona ? 'Edit persona' : 'New persona'} width="max-w-2xl">
      <div className="space-y-3">
        <Field label="Name">
          <Input value={form.name} onChange={set('name')} placeholder="Fortune 500 Procurement Director" />
        </Field>
        <Field label="Description">
          <Textarea rows={2} value={form.description} onChange={set('description')} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Job titles" hint="Comma separated. Used to auto-assign imported prospects.">
            <Input value={form.job_titles} onChange={set('job_titles')} placeholder="procurement, purchasing" />
          </Field>
          <Field label="Industries" hint="Comma separated.">
            <Input value={form.industry_focus} onChange={set('industry_focus')} placeholder="defense, aerospace" />
          </Field>
        </div>

        <Field label="Email prompt" hint="How Claude should write emails to this persona.">
          <Textarea
            rows={5}
            value={form.email_prompt}
            onChange={set('email_prompt')}
            placeholder="Be concise and credible. Lead with FAR compliance and vendor consolidation. Their pain is managing a long tail of language vendors."
          />
        </Field>

        <Field label="LinkedIn DM prompt">
          <Textarea rows={3} value={form.linkedin_dm_prompt} onChange={set('linkedin_dm_prompt')} />
        </Field>

        <Field label="Post idea prompt">
          <Textarea
            rows={3}
            value={form.linkedin_post_idea_prompt}
            onChange={set('linkedin_post_idea_prompt')}
          />
        </Field>

        {(save.error || remove.error) && <ErrorNote message={(save.error ?? remove.error) as string} />}

        <div className="flex justify-between gap-2 pt-1">
          {persona ? (
            <Button variant="danger" loading={remove.pending} onClick={() => void remove.run()}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              loading={save.pending}
              disabled={!form.name.trim()}
              onClick={() => void save.run()}
            >
              Save persona
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ProspectsTab({ campaignId }: { campaignId: string }) {
  const prospects = useApi<{ prospects: Prospect[]; total: number }>(
    `/api/prospects?campaign_id=${campaignId}&limit=200`,
  );

  if (prospects.loading) return <Loading />;
  if (prospects.error) return <ErrorNote message={prospects.error} onRetry={prospects.reload} />;

  const rows = prospects.data?.prospects ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No prospects in this campaign"
        description="Import a CSV and pick this campaign, or assign existing prospects from the prospects page."
        action={
          <Link
            to="/prospects"
            className="inline-flex items-center rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            Go to prospects
          </Link>
        }
      />
    );
  }

  return (
    <Card bodyClassName="p-0">
      <Table>
        <thead>
          <tr>
            <Th>Prospect</Th>
            <Th>Persona</Th>
            <Th>Status</Th>
            <Th>Touches</Th>
            <Th>Last contact</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {rows.map((prospect) => (
            <tr key={prospect.id} className="hover:bg-ink-50/60">
              <Td>
                <Link
                  to={`/prospects/${prospect.id}`}
                  className="font-medium text-ink-900 hover:text-accent-700"
                >
                  {`${prospect.first_name} ${prospect.last_name}`.trim() || 'Unnamed'}
                </Link>
                <div className="text-xs text-ink-500">{prospect.company_name}</div>
              </Td>
              <Td className="text-sm">{prospect.persona_name ?? '—'}</Td>
              <Td>
                <StatusBadge status={prospect.status} />
              </Td>
              <Td className="tabular-nums">{prospect.touch_count ?? 0}</Td>
              <Td className="text-xs text-ink-500">{relative(prospect.last_contacted_at)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function AnalyticsTab({ campaignId }: { campaignId: string }) {
  const funnel = useApi<Funnel>(`/api/analytics/funnel?campaign_id=${campaignId}`);
  const roi = useApi<{ rows: RoiRow[] }>(`/api/analytics/roi?campaign_id=${campaignId}`);
  const winLoss = useApi<{
    by_outcome: Array<{ outcome: string; count: number; total_value: number }>;
    reasons: Array<{ outcome: string; reason: string; prospect: string; closed_at: string }>;
    top_objections: Array<{ objection: string; count: number }>;
  }>(`/api/analytics/win-loss?campaign_id=${campaignId}`);

  if (funnel.loading) return <Loading />;
  if (funnel.error) return <ErrorNote message={funnel.error} onRetry={funnel.reload} />;
  if (!funnel.data) return null;

  const row = roi.data?.rows[0];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Prospects" value={funnel.data.prospects} sub={`${funnel.data.contacted} contacted`} />
        <Stat label="Reply rate" value={percent(funnel.data.reply_rate, 1)} sub={`${funnel.data.replied} replies`} />
        <Stat label="Warm" value={funnel.data.warm} tone="positive" sub={percent(funnel.data.warm_rate)} />
        <Stat label="Won value" value={money(row?.won_value ?? 0)} tone="positive" sub={`${funnel.data.won} won`} />
      </div>

      <Card title="Funnel">
        <FunnelBars funnel={funnel.data} />
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Top objections">
          {winLoss.data?.top_objections.length ? (
            <ul className="space-y-1.5">
              {winLoss.data.top_objections.map((objection) => (
                <li key={objection.objection} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-ink-700">{objection.objection}</span>
                  <Badge tone="amber">{objection.count}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">
              Objections appear here once Claude has analysed some replies.
            </p>
          )}
        </Card>

        <Card title="Win / loss reasons">
          {winLoss.data?.reasons.length ? (
            <ul className="space-y-2.5">
              {winLoss.data.reasons.slice(0, 8).map((reason, index) => (
                <li key={index} className="text-sm">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={reason.outcome} />
                    <span className="text-ink-800">{reason.prospect}</span>
                  </div>
                  <p className="mt-0.5 text-ink-600">{reason.reason}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-500">No closed deals recorded yet.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

export function FunnelBars({ funnel }: { funnel: Funnel }) {
  const stages: Array<[string, number, string]> = [
    ['Prospects', funnel.prospects, 'bg-ink-300'],
    ['Contacted', funnel.contacted, 'bg-accent-400'],
    ['Replied', funnel.replied, 'bg-violet-400'],
    ['Warm', funnel.warm, 'bg-emerald-400'],
    ['Won', funnel.won, 'bg-emerald-600'],
  ];
  const max = Math.max(...stages.map(([, value]) => value), 1);

  return (
    <div className="space-y-2">
      {stages.map(([label, value, color]) => (
        <div key={label} className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-xs text-ink-600">{label}</span>
          <div className="h-6 flex-1 overflow-hidden rounded bg-ink-100">
            <div
              className={cx('h-full rounded transition-all', color)}
              style={{ width: `${Math.max((value / max) * 100, value > 0 ? 3 : 0)}%` }}
            />
          </div>
          <span className="w-14 shrink-0 text-right text-sm font-medium tabular-nums text-ink-800">
            {value}
          </span>
          <span className="w-14 shrink-0 text-right text-xs text-ink-500">
            {funnel.prospects ? percent(value / funnel.prospects) : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}
